import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import SvgIconRewardsProgram from '../../../static/icons/svgIconRewardsProgram';
import { connectWithRouter } from 'helpers/connectWithRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { ReduxState } from '../../../reducers/types';
import { getOrganizationSettings } from 'actions/Companies/Settings/GetOrganizationSettings';
import { OrganizationSettingsState } from 'reducers/OrganizationSettings';
import RewardProgramConversion from 'components/CompanyDetails/CommercialRewards/RewardProgramConversion';

const styles = require('./styles.pcss');

type Props = {
  company?: any;
  organizationId?: string;
  organizationSettings?: OrganizationSettingsState | RewardSettingsMap;
  getOrganizationSettings: Function;
  compact?: boolean;
};

type RewardSettingsMap = Record<string, unknown>;

type SelectedRewardProgram = 'none' | 'points' | 'rebate';

type RewardTypeSetting = {
  id?: string;
  value?: unknown;
};

const normalizeRewardProgramValue = (value: unknown): SelectedRewardProgram => {
  if (typeof value !== 'string') {
    return 'none';
  }

  const normalized = value.toLowerCase().trim();
  if (normalized === 'points' || normalized === 'rebate' || normalized === 'none') {
    return normalized;
  }

  return 'none';
};

const getSettingsMap = (
  organizationSettings: OrganizationSettingsState | RewardSettingsMap | undefined,
): RewardSettingsMap => {
  if (!organizationSettings || typeof organizationSettings !== 'object') {
    return {};
  }

  if (
    'settings' in organizationSettings &&
    organizationSettings.settings &&
    typeof organizationSettings.settings === 'object'
  ) {
    return organizationSettings.settings as RewardSettingsMap;
  }

  return organizationSettings as RewardSettingsMap;
};

const RewardsProgram: React.FC<Props> = ({
  company,
  organizationId,
  organizationSettings,
  getOrganizationSettings,
  compact = false,
}) => {
  const [selectedRewardProgram, setSelectedRewardProgram] =
    useState<SelectedRewardProgram>('none');
  const [rewardProgramSettingId, setRewardProgramSettingId] = useState<string>('');

  useEffect(() => {
    const resolvedOrganizationId = organizationId || company?.id;
    if (resolvedOrganizationId) {
      getOrganizationSettings(resolvedOrganizationId);
    }
  }, [company?.id, organizationId, getOrganizationSettings]);

  useEffect(() => {
    const settings = getSettingsMap(organizationSettings);
    const rewardType = settings['rewardType'];
    const rewardTypeValue =
      rewardType && typeof rewardType === 'object' && 'value' in rewardType
        ? (rewardType as RewardTypeSetting).value
        : rewardType;

    const rewardTypeSettingId =
      rewardType && typeof rewardType === 'object' && 'id' in rewardType
        ? (rewardType as RewardTypeSetting).id
        : undefined;

    setSelectedRewardProgram(normalizeRewardProgramValue(rewardTypeValue));
    setRewardProgramSettingId(typeof rewardTypeSettingId === 'string' ? rewardTypeSettingId : '');
  }, [organizationSettings]);

  return (
    <Paper className={`${styles.paper}${compact ? ` ${styles.compactPaper}` : ''}`}>
      <div className={styles.commercialRewardsHeader}>
        <div className={styles.icon}>
          <SvgIconRewardsProgram
            width={32}
            height={32}
          />
        </div>
        <div className={styles.text}>
          <h2>Commercial Rewards</h2>
          <div className={styles.description}>
            Details for the company rewards program.
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <RewardProgramConversion
          organizationId={organizationId || company?.id}
          rewardProgramSettingId={rewardProgramSettingId}
          selectedRewardProgram={selectedRewardProgram}
          compact={compact}
          embedded={true}
          onConversionSuccess={() =>
            getOrganizationSettings(organizationId || company?.id)
          }
        />
      </div>
    </Paper>
  );
};

function mapStateToProps(state: ReduxState) {
  return {
    company: state.company.company,
    organizationSettings: state.organizationSettings,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getOrganizationSettings,
    },
    dispatch,
  );
}

export default connectWithRouter(mapStateToProps, mapDispatchToProps)(RewardsProgram);
